import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import { tasksAndSubtasksApi } from "./tasks-and-subtasks.api";
import { SubTaskInfo, TaskInfo } from "../../pages/TasksAndSubTasks/schema/tasks-and-subtasks";

interface TasksState {
	tasks: TaskInfo[];
	subTasks: SubTaskInfo[];
}

const initialState: TasksState = {
	tasks: [],
	subTasks: [],
};

export const tasksAndSubtasksSlice = createSlice({
	name: "tasks-and-subtasks",
	initialState,
	reducers: {
		updateTask: (state, action: PayloadAction<{ tasks: TaskInfo[]; subtasks: SubTaskInfo[] }>) => {
			if (!action.payload) {
				return;
			}

			state.tasks = action.payload.tasks || [];
			state.subTasks = action.payload.subtasks || [];
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			tasksAndSubtasksApi.endpoints.getAllTasks.matchFulfilled,
			(state, { payload }) => {
				state.tasks = payload ?? [];
			}
		);

		builder.addMatcher(
			tasksAndSubtasksApi.endpoints.getAllSubTasks.matchFulfilled,
			(state, { payload }) => {
				state.subTasks = payload ?? [];
			}
		);

		builder.addMatcher(
			tasksAndSubtasksApi.endpoints.createTask.matchFulfilled,
			(state, { payload }) => {
				if (payload?.id) {
					state.tasks = [payload, ...state.tasks];
				}
			}
		);

		builder.addMatcher(
			tasksAndSubtasksApi.endpoints.updateTask.matchFulfilled,
			(state, { payload }) => {
				if (payload?.id) {
					const index = state.tasks.findIndex((task) => task.id === payload.id);

					if (index !== -1) {
						state.tasks[index] = payload;
					}
				}
			}
		);

		builder.addMatcher(
			tasksAndSubtasksApi.endpoints.deleteTask.matchFulfilled,
			(state, { payload }) => {
				if (payload?.id) {
					state.tasks = state.tasks.filter((task) => task.id !== payload.id);
				}
			}
		);

		builder.addMatcher(
			tasksAndSubtasksApi.endpoints.updateSubTask.matchFulfilled,
			(state, { payload }) => {
				const index = state.subTasks.findIndex((s) => s.id === payload.id);

				if (index !== -1) {
					state.subTasks[index] = payload;
				}
			}
		);

		builder.addMatcher(
			tasksAndSubtasksApi.endpoints.deleteSubTask.matchFulfilled,
			(state, { payload }) => {
				state.subTasks = state.subTasks.filter((s) => s.id !== payload.id);
			}
		);
	},
});

export const { updateTask } = tasksAndSubtasksSlice.actions;
export const tasksAndSubtasksReducer = tasksAndSubtasksSlice.reducer;

export const tasksSelector = (state: RootState) => state[tasksAndSubtasksSlice.name].tasks;
export const subTasksSelector = (state: RootState) => state[tasksAndSubtasksSlice.name].subTasks;

export const tasksWithSubTasksSelector = (state: RootState) => {
	const { tasks, subTasks } = state[tasksAndSubtasksSlice.name];

	return tasks.map((task) => ({
		...task,
		subTasks: subTasks.filter((sub) => sub.taskId === task.id),
	}));
};
